import React from 'react';
import clsx from 'clsx';
import { Colors, FontSize, FontWeight, Spacing } from '@common/tokens';
import { StepIndicator, type StepStatusType } from './_step-indicator';

/**
 * A stalling bar decelerates over this window, easing toward — but never
 * reaching — the end until the step advances or the caller closes the modal.
 */
const STALL_DURATION_MS = 60_000;

/** A single narrated step: status indicator, label, and an active progress bar. */
export function LoadingStep({
  label,
  status,
  progressKey,
  durationMs,
  stalls,
}: {
  label: string;
  status: StepStatusType;
  /** Re-keys the progress bar so its fill animation restarts on each advance. */
  progressKey: number;
  /** Duration of the current step's progress-bar fill, in ms. */
  durationMs: number;
  /**
   * Whether this step has an indeterminate duration. When true the bar
   * decelerates and never completes on its own — used for the held final step
   * in timed mode and the active step in controlled mode. The step advancing
   * (or the caller closing the modal) is what finishes it.
   */
  stalls: boolean;
}) {
  return (
    <div
      className={clsx('wk-full-screen-loading-grid__step', {
        'wk-full-screen-loading-grid__step--current': status === 'current',
        'wk-full-screen-loading-grid__step--done': status === 'done',
      })}
    >
      <div className="wk-full-screen-loading-grid__step-indicator">
        <StepIndicator status={status} />
      </div>
      <div className="wk-full-screen-loading-grid__step-body">
        <span className="wk-full-screen-loading-grid__step-label">{label}</span>
        {status === 'current' && (
          <div
            key={progressKey}
            className={clsx('wk-full-screen-loading-grid__progress', {
              'wk-full-screen-loading-grid__progress--stalling': stalls,
            })}
          />
        )}
      </div>

      <style jsx>{`
        .wk-full-screen-loading-grid__step {
          display: flex;
          align-items: flex-start;
          gap: ${Spacing.sm}px;
        }
        .wk-full-screen-loading-grid__step-indicator {
          width: 16px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .wk-full-screen-loading-grid__step-body {
          display: flex;
          flex-direction: column;
          gap: 5px;
          flex: 1;
          min-width: 0;
        }
        .wk-full-screen-loading-grid__step-label {
          font-size: ${FontSize.md};
          line-height: 1.4;
          color: ${Colors.midnightLighter};
          font-weight: 400;
          transition:
            color 0.4s ease,
            font-weight 0.4s ease;
        }
        .wk-full-screen-loading-grid__step--current
          .wk-full-screen-loading-grid__step-label {
          color: ${Colors.textDefault};
          font-weight: ${FontWeight.medium};
        }
        /* Thin bar that fills left-to-right over the current step's duration. */
        .wk-full-screen-loading-grid__progress {
          height: 2px;
          border-radius: 2px;
          background: ${Colors.elm};
          width: 0%;
          animation: fill-bar ${durationMs}ms linear forwards;
        }
        /*
         * Stalling bar: eases the bar toward ~92% over a long window, so it
         * decelerates and never reaches the end until the step advances or the
         * caller closes the modal. The curve ramps up gently at the start
         * before settling into the long creep.
         */
        .wk-full-screen-loading-grid__progress--stalling {
          animation: stall-bar ${STALL_DURATION_MS}ms
            cubic-bezier(0.15, 0.35, 0.25, 1) forwards;
        }
        @keyframes fill-bar {
          from {
            width: 0%;
            opacity: 1;
          }
          95% {
            width: 100%;
            opacity: 1;
          }
          to {
            width: 100%;
            opacity: 0;
          }
        }
        @keyframes stall-bar {
          from {
            width: 0%;
          }
          to {
            width: 92%;
          }
        }
      `}</style>
    </div>
  );
}
